/**
 * History Rail — compare modes (HR-4).
 *
 * Replaces three differently-named controls with one segmented choice. The
 * modal had "See what changed" (in a banner), "Compare to now" and "Visual diff
 * vs vN" — three names for two ideas, and no copy anywhere explaining that the
 * first two are STRUCTURAL and the third is PIXELS.
 *
 * Modes:
 *   this     — the version, plain
 *   changes  — the same canvas with the sections this version CHANGED outlined
 *
 * "Changes" reuses AR-6's runtime chrome rather than inventing a second one:
 * `set-changed-sections` in `editing/iframe-bridge-layers/message-handler.js`
 * stamps `data-pb-section-changed` and `renderer/css-assembler.js` draws it. The
 * rows are a FULL REPLACEMENT, replayed on `iframe:bridge-ready`, and the
 * attributes never enter stored HTML — that last property is load-bearing and
 * must survive any change here.
 *
 * WHAT "CHANGED" MEANS, stated because the modal never did: changes are
 * measured against the version IMMEDIATELY BEFORE this one, so the answer is
 * "what did this save do", not "how does this differ from your work now".
 *
 * WHAT CANNOT BE SHOWN: a section this version REMOVED has no wrapper in the
 * rendered DOM to outline. Those are counted and named in the bar rather than
 * silently dropped — a highlight that quietly omits deletions is worse than no
 * highlight, because it reads as "nothing else happened".
 */

import { diffVersionStates } from '../../shared/version-diff.js';
import { getVersionState } from '../../core/version-state-cache.js';
import { getCategoryMap } from '../../shared/section-display-name.js';

/** Section kinds that exist in the NEW state, and so have something to outline. */
const VISIBLE_KINDS = new Set(['edited', 'added', 'moved', 'modernized']);

export function createCompareController({ projectId, hashOf = null, deps = {} }) {
  const readVersion = deps.getVersionState || getVersionState;
  const diff = deps.diffVersionStates || diffVersionStates;
  /**
   * version → contentHash, for the fold short-circuit below. Optional: without
   * it every pair is diffed for real, which is correct but slower.
   * @type {(version: number) => string|null}
   */
  const hashFor = typeof hashOf === 'function' ? hashOf : () => null;

  /**
   * Section names come from the same map the section list uses, so a caption
   * says "Hero" rather than "hero". Best-effort and never awaited: an empty map
   * degrades labels to raw category keys, which is legible but ugly — and the
   * modal passed this for exactly that reason. Dropping it was a quiet
   * regression that only reading a real caption revealed.
   */
  let categoryMap = deps.categoryMap || {};
  if (!deps.categoryMap) {
    (deps.loadJSON ? deps.loadJSON('data/category-map.json') : getCategoryMap())
      .then((map) => {
        categoryMap = map || {};
      })
      .catch(() => {
        /* expected-flow fallback: labels degrade to category keys */
      });
  }

  /** version → {rows, removed, baseline} — memoised for the rail's lifetime. */
  const memo = new Map();
  let token = 0;

  /**
   * @param {number} version
   * @param {number|null} previous — the version immediately before it, if loaded
   */
  async function changesFor(version, previous) {
    const key = `${version}:${previous ?? 'none'}`;
    if (memo.has(key)) return memo.get(key);
    if (!Number.isInteger(previous)) {
      const first = { rows: [], removed: 0, baseline: null, reason: 'first' };
      memo.set(key, first);
      return first;
    }

    // THE FOLDING INVARIANT, MADE VISIBLE — ported from the modal in HR-7 after
    // its suite (project-history-diff-tests.js) was retired and this turned out
    // to be the one thing that suite guarded which nothing here did.
    //
    // Two adjacent versions with the SAME non-null contentHash cannot differ, so
    // the answer is known without reading either bundle. That is not a micro-
    // optimisation here the way it was in the modal: HR-5 fills a summary for
    // EVERY row in the window in the background, and a run of N identical saves
    // — which is precisely the case folding exists for — was costing 2N bundle
    // reads to print "No changes" N times.
    //
    // A NULL hash never short-circuits. A null tells us nothing (pre-006 rows),
    // and treating null == null as "equal" would report real changes as none.
    const hash = hashFor(version);
    const priorHash = hashFor(previous);
    if (hash && priorHash && hash === priorHash) {
      const same = { rows: [], removed: 0, baseline: previous, reason: null };
      memo.set(key, same);
      return same;
    }

    const [before, after] = await Promise.all([
      readVersion(projectId, previous),
      readVersion(projectId, version)
    ]);
    const result = diff(before, after, { categoryMap });
    const rows = [];
    let removed = 0;

    for (const page of result.pages || []) {
      for (const section of page.sections || []) {
        if (section.kind === 'removed') {
          removed += 1;
          continue;
        }
        if (!VISIBLE_KINDS.has(section.kind) || !section.instanceId) continue;
        rows.push({
          instanceId: section.instanceId,
          pageId: page.pageId || null,
          label: labelFor(section)
        });
      }
    }
    // Shared nav/footer carry `pageId: 'shared'`, which the shell's filter
    // passes on every page — the same convention the agent rows use.
    for (const [category, entry] of Object.entries(result.shared || {})) {
      if (!entry) continue;
      if (entry.kind === 'removed') {
        removed += 1;
        continue;
      }
      if (entry.instanceId) {
        rows.push({
          instanceId: entry.instanceId,
          pageId: 'shared',
          label: labelFor(entry, category)
        });
      }
    }

    const out = { rows, removed, baseline: previous, reason: null };
    memo.set(key, out);
    return out;
  }

  return {
    /**
     * Resolve the chrome for a version. Token-guarded: a mode flip or a new
     * selection while this is in flight must not paint the old answer.
     * @returns {Promise<{rows: Array, removed: number, baseline: number|null, stale: boolean}>}
     */
    async resolve(version, previous) {
      const mine = ++token;
      const result = await changesFor(version, previous);
      return { ...result, stale: mine !== token };
    },

    /**
     * Same computation, but NOT token-guarded: row summaries are filled for the
     * whole window in the background and must not cancel each other, nor be
     * cancelled by a selection change. The memo means the canvas chrome and the
     * summaries share every fetch.
     */
    summaryFor(version, previous) {
      return changesFor(version, previous);
    },

    /** Invalidate in-flight work without computing anything. */
    cancel() {
      token += 1;
    }
  };
}

function labelFor(section, fallback = 'Section') {
  const name = section.label || fallback;
  if (section.kind === 'added') return `${name} · added`;
  if (section.kind === 'moved') return `${name} · moved`;
  return name;
}

/**
 * The sentence the modal never wrote. `visual-diff-modal.js`'s own header says
 * "'Now' is deliberately NOT comparable" — only saved versions are keyable and
 * immutable — while the modal offered a "Compare to now" button beside a
 * "Visual diff" button and explained neither. A user reads that as a bug.
 */
export function describeChanges({ rows, removed, baseline, reason }, { terse = false } = {}) {
  if (reason === 'first')
    return terse ? 'First version' : 'First version — nothing to compare against';
  const changed = rows.length;
  if (!changed && !removed) return terse ? 'No changes' : `No visible changes from v${baseline}`;

  // The TERSE form is the row's own caption, and it names the sections rather
  // than counting them: "Hero, Services · +1 section" is what turns a list of
  // numbers into a list of events. Counting ("2 sections changed") is what the
  // review bar says, because there the canvas is already showing you which.
  if (terse) {
    // The name list covers EDITS only, and additions are counted separately.
    // Naming everything and then also counting the additions double-reports
    // them — "Hero, Services +2 more · +1 section" makes a reader hunt for a
    // fifth change that does not exist.
    const edited = rows.filter((r) => !/ · added$/.test(r.label));
    const added = rows.length - edited.length;
    const names = edited.map((r) => r.label.replace(/ · moved$/, ''));
    const parts = [];
    if (names.length) {
      const shown = names.slice(0, 2).join(', ');
      const more = names.length - 2;
      parts.push(more > 0 ? `${shown} +${more} more` : shown);
    }
    if (added) parts.push(`+${added} section${added === 1 ? '' : 's'}`);
    if (removed) parts.push(`−${removed}`);
    return parts.join(' · ');
  }

  const parts = [];
  if (changed) parts.push(`${changed} section${changed === 1 ? '' : 's'} changed`);
  if (removed) parts.push(`${removed} removed (not shown — no longer on the page)`);
  return `Since v${baseline}: ${parts.join(' · ')}`;
}
